/* Progressive enhancement.
 *
 * Open intake: anyone can submit (form or agent) without an account.
 * Free visitors see FREE_ROWS + a blurred teaser under the gate; Adjacent org
 * members unlock the full scroll, search, and filters.
 *
 * Row shape and the derived display values live in disputes-data.js, shared
 * with the Observable notebook so the two views cannot disagree about a case.
 */

import {
  cellOf,
  esc,
  failureSlug,
  filterSortRows,
  fmtDate,
  fmtMoney,
  normalizeRow,
  titleCase,
} from './disputes-data.js';

/* Free visitors read this many interactive rows... */
const FREE_ROWS = 20;

/* ...then this many locked rows behind the gate. Blur ramps across the last
 * free rows and through the teaser so the list dissolves instead of cutting.
 *
 * Guests never actually see these: the server caps them at PREVIEW_ROWS (=
 * FREE_ROWS), because a blurred row is still a readable row in the JSON. The
 * teaser only fills in for entitled members on a filtered view. These numbers
 * shape the blur — they are not the access control. */
const TEASER_ROWS = 14;

/* Soft blur begins this many free rows before the lock (still clickable). */
const SOFT_BLUR_LEAD = 8;

/* The ramp stops deepening here so the tail stays legible-as-texture all the way
 * down. The opacity term reaches zero around depth 26, and the frame's veil
 * dims the bottom band further — past ~12 the two compound into blank paper. */
const DEPTH_CAP = 12;

/* Ghost tail. The server hands guests exactly PREVIEW_ROWS, so on a tall screen
 * the record would stop mid-frame with white space beside the gate. Ghost rows
 * are skeleton bars — no case data, nothing to un-blur — appended purely so the
 * list dissolves into the gate and reads as continuing past it. */
const GHOST_ROWS_MIN = 8;
const GHOST_ROWS_MAX = 48;
/* Fraction of the frame to overfill past the fold: the last ghost must be cut
 * off by the frame, and on mobile the tail also has to be long enough to scroll
 * into, since reaching it is what reveals the sheet. */
const GHOST_OVERFILL = { desktop: 0.25, mobile: 0.8 };

/**
 * App is mounted at /disputes on labs.adjacent.markets (and locally under the
 * same prefix). API routes live under that base: /disputes/api/*.
 * VITE_DISPUTES_API overrides the origin+prefix when the UI is split from the API.
 */
const APP_BASE = String(import.meta.env.BASE_URL || '/').replace(/\/$/, '');
const API_ROOT = String(import.meta.env?.VITE_DISPUTES_API ?? '').replace(/\/$/, '') || APP_BASE;
const apiUrl = (path) => `${API_ROOT}${path.startsWith('/') ? path : `/${path}`}`;

const CLERK_PUBLISHABLE_KEY = import.meta.env?.VITE_CLERK_PUBLISHABLE_KEY ?? '';
/** Adjacent internal Clerk organization id (must match worker CLERK_ORG_ID). */
const CLERK_ORG_ID = import.meta.env?.VITE_CLERK_ORG_ID ?? '';

/** Canonical production origin (absolute: agents run wherever they run). The
 *  submit and record endpoints are documented in prompt.md, not here. */
const PUBLIC_APP = 'https://labs.adjacent.markets/disputes';
const PUBLIC_PROMPT_URL = `${PUBLIC_APP}/prompt.md`;

const ACCOUNT_URL = 'https://adjacent.markets/settings/account';
/** Signed-out Login/Sign in — billing redirects to Clerk sign-in when needed. */
const LOGIN_URL = 'https://adjacent.markets/settings/billing';
const IS_DEV = Boolean(import.meta.env?.DEV);

const $ = (s) => document.querySelector(s);

/** Public table columns only — everything else is detail-only. */
const TABLE_COLS = [
  { id: 'market', label: 'Market', sort: 'market', type: 'text' },
  { id: 'venue', label: 'Venue', sort: 'venue', type: 'pill' },
  { id: 'date', label: 'Date', sort: 'date', type: 'date', num: true, sortable: true },
  { id: 'failure', label: 'Reason', sort: 'failure', type: 'pill' },
  { id: 'notional', label: 'Est. Notional', sort: 'notional', type: 'money', num: true, sortable: true },
];

/** Long-form fields only — meta row already covers venue/date/notional/failure. */
const DETAIL_PROPS = [
  'Summary',
  'What went wrong',
  'What happened',
  'Outcome',
  'Lesson',
];

const DETAIL_SKIP = new Set([
  'Market', 'Name', 'Title', 'Venue', 'Month', 'Notional', 'Failure type',
  'Status', 'Slug', 'Kind', 'Contact', 'Email',
  // Submissions columns already shown in the meta row or the sources list.
  'Review', 'Resolution Date', 'Dispute Amount', 'Submitted',
  // URL columns render in the Sources list instead of as prose blocks.
  'Market Link', 'Links', 'Link', 'Source',
]);

let rows = [];
/* Set from the API response, never inferred from the Clerk session: the server
 * is the only thing that knows how many rows it actually sent. */
let entitled = false;
const MOBILE_GATE_MQ = window.matchMedia('(max-width: 720px)');
let mobileGateArmed = false;
/* Dismissing the sheet is a promise for the session: it does not slide back. */
let mobileGateDismissed = false;
let teardownMobileGate = null;
let clerk = null;

const state = { q: '', venue: '', failure: '', sort: 'date', dir: 'desc' };
const tableSection = $('.table');

const filteredRows = () => filterSortRows(rows, state);

function renderHead() {
  $('#cols').innerHTML = TABLE_COLS.map((col) => {
    // Only the two numeric columns are orderable; the rest are labels.
    const canSort = entitled && col.sortable;
    const active = canSort && state.sort === col.sort;
    const dirLabel = active ? ` (${state.dir.toUpperCase()})` : '';
    // Numeric columns right-align their cells; the header has to follow.
    const cls = col.num ? ' class="num"' : '';
    if (!canSort) {
      return `<th scope="col"${cls}><span class="th-label">${esc(col.label)}</span></th>`;
    }
    return `<th scope="col"${cls}${active ? ` data-dir="${esc(state.dir)}"` : ''}>
      <button type="button" data-sort="${esc(col.sort)}">${esc(col.label)}${dirLabel}</button>
    </th>`;
  }).join('');
}

function syncControls() {
  const locked = !entitled;
  for (const el of [$('#search'), $('#filter-venue'), $('#filter-failure')]) {
    if (!el) continue;
    el.disabled = locked;
    el.title = locked ? 'Sign in to search and filter' : '';
  }
  document.querySelector('.db-toolbar')?.classList.toggle('is-locked', locked);
  document.querySelector('.db-frame')?.classList.toggle('is-free', locked);
}

function renderCell(row, col) {
  if (col.id === 'market') {
    return `<td class="market">${esc(row.market)}</td>`;
  }
  if (col.id === 'venue') {
    return row.venue
      ? `<td class="venue">${esc(row.venue)}</td>`
      : '<td class="db-empty">—</td>';
  }
  if (col.id === 'date') {
    return `<td class="num">${fmtDate(row.date)}</td>`;
  }
  if (col.id === 'failure') {
    if (!row.failure) return '<td class="db-empty">—</td>';
    const slug = failureSlug(row.failure);
    return `<td><span class="db-pill db-pill--failure" data-failure="${slug}">${esc(row.failure)}</span></td>`;
  }
  if (col.id === 'notional') {
    return `<td class="num">${esc(fmtMoney(row.notional))}</td>`;
  }
  return '<td class="db-empty">—</td>';
}

function fillFilters() {
  const venues = [...new Set(rows.map((r) => r.venue).filter(Boolean))].sort();
  const failures = [...new Set(rows.map((r) => r.failure).filter(Boolean))].sort();

  const venueSel = $('#filter-venue');
  const failureSel = $('#filter-failure');
  if (venueSel) {
    const cur = state.venue;
    venueSel.innerHTML =
      '<option value="">All venues</option>' +
      venues.map((v) => `<option value="${esc(v)}">${esc(v)}</option>`).join('');
    venueSel.value = cur;
  }
  if (failureSel) {
    const cur = state.failure;
    failureSel.innerHTML =
      '<option value="">All reasons</option>' +
      failures.map((v) => `<option value="${esc(v)}">${esc(v)}</option>`).join('');
    failureSel.value = cur;
  }
}

/**
 * Position on the blur ramp for the guest row at index `i`: flat through the
 * open rows, one step per row across the last SOFT_BLUR_LEAD of them, then
 * continuing at the same rate through the locked tail. One function so free
 * rows, locked teasers, and ghosts cannot drift apart at the seams.
 */
function rampDepth(i) {
  if (i < FREE_ROWS) return Math.max(0, i - (FREE_ROWS - SOFT_BLUR_LEAD) + 1);
  return SOFT_BLUR_LEAD + (i - FREE_ROWS);
}

const depthVar = (ramp) => Math.min(ramp, DEPTH_CAP);

/**
 * Guest-list blur ramp.
 * - ramp 0: sharp free row
 * - ramp > 0: still interactive, light progressive blur into the gate
 * - locked: inert teaser continuing the same ramp
 */
function rowHtml(row, ramp = 0, locked = false) {
  const cells = TABLE_COLS.map((col) => renderCell(row, col)).join('');
  let attrs;
  if (locked) {
    attrs = `class="locked" inert style="--depth:${depthVar(ramp)}"`;
  } else if (ramp > 0) {
    attrs = `class="soft-blur" tabindex="0" style="--depth:${depthVar(ramp)}"`;
  } else {
    attrs = 'tabindex="0"';
  }
  return `<tr data-id="${esc(row.id)}" ${attrs}>${cells}</tr>`;
}

/* Irregular so the skeleton reads as wrapped market titles rather than a bar
 * chart. Reason pills reuse the real failure scale, so the tail keeps the
 * table's colour rhythm under blur. */
const GHOST_TITLE_WIDTHS = [88, 72, 95, 64, 80, 58, 91, 76];
const GHOST_PILL_WIDTHS = [7.5, 5.5, 6.5, 8, 6, 7];
const GHOST_FAILURES = [
  'definitional-vagueness',
  'scalar-carve-out',
  'disclosure-timing',
  'source-dependence',
  'venue-discretion',
  'oracle-manipulation',
];

const pick = (list, i) => list[i % list.length];

function ghostCell(col, i) {
  if (col.id === 'market') {
    return `<td class="market"><span class="db-ghost" style="--w:${pick(GHOST_TITLE_WIDTHS, i)}%"></span></td>`;
  }
  if (col.id === 'venue') {
    return `<td class="venue"><span class="db-ghost" style="--w:${i % 2 ? 3.4 : 4.6}rem"></span></td>`;
  }
  if (col.id === 'failure') {
    return `<td><span class="db-pill db-pill--failure db-ghost-pill" data-failure="${pick(GHOST_FAILURES, i)}" style="--w:${pick(GHOST_PILL_WIDTHS, i)}rem"></span></td>`;
  }
  return `<td class="num"><span class="db-ghost" style="--w:${col.id === 'date' ? 3.2 : 2.9}rem"></span></td>`;
}

function ghostRowHtml(ramp, i) {
  const cells = TABLE_COLS.map((col) => ghostCell(col, i)).join('');
  return `<tr class="locked is-ghost" inert aria-hidden="true" style="--depth:${depthVar(ramp)}">${cells}</tr>`;
}

/**
 * Shortest tail worth rendering. On mobile the gate is a sheet fixed over the
 * bottom of the frame, so the tail also has to be at least as tall as the sheet:
 * it is what the last real row scrolls up through to clear it.
 */
function ghostFloor(rowH) {
  if (!MOBILE_GATE_MQ.matches) return GHOST_ROWS_MIN;
  const sheetH = $('#gate')?.getBoundingClientRect().height ?? 0;
  return Math.max(GHOST_ROWS_MIN, Math.ceil((sheetH + rowH) / rowH));
}

/**
 * Extend the guest list with ghost rows until the blur runs past the bottom of
 * the frame. Measured rather than fixed: how many rows fit depends on the
 * viewport and on how many lines the real titles wrapped to.
 */
function padGuestList() {
  const tbody = $('#rows');
  const scroller = $('.db-scroll');
  if (!tbody || !scroller) return;
  for (const ghost of tbody.querySelectorAll('tr.is-ghost')) ghost.remove();
  if (entitled) return;
  const real = tbody.querySelectorAll('tr').length;
  // An empty corpus stays empty: ghosts continue a record, they don't invent one.
  if (!real) return;
  const room = scroller.clientHeight;
  if (!room) return;
  // The table's own height, not scrollHeight: a scroller never reports content
  // shorter than its box, which is exactly the case being measured here.
  const contentH = scroller.querySelector('.db-table')?.getBoundingClientRect().height ?? 0;
  const overfill = MOBILE_GATE_MQ.matches ? GHOST_OVERFILL.mobile : GHOST_OVERFILL.desktop;
  const deficit = room * (1 + overfill) - contentH;
  // Ghosts are one line each, so the real rows' average height (titles wrap)
  // cannot say how many of them fill a gap. Lay one down and measure it.
  tbody.insertAdjacentHTML('beforeend', ghostRowHtml(rampDepth(real), 0));
  const probe = tbody.lastElementChild;
  const ghostH = probe.getBoundingClientRect().height || 32;
  const count = Math.min(
    GHOST_ROWS_MAX,
    Math.max(ghostFloor(ghostH), Math.ceil(deficit / ghostH)),
  );
  let html = '';
  for (let i = 1; i < count; i += 1) html += ghostRowHtml(rampDepth(real + i), i);
  if (html) probe.insertAdjacentHTML('afterend', html);
}

function render() {
  const empty = $('#db-empty');
  syncControls();
  // Hero submit stays public; gate still carries the free-tier unlock CTA.
  const memberActions = $('#member-actions');
  if (memberActions) memberActions.hidden = false;

  if (!rows.length) {
    $('#rows').innerHTML = '';
    if (empty) {
      empty.hidden = false;
      empty.textContent = 'No published cases yet.';
    }
    // Still show the gate so guests can submit even on an empty corpus.
    $('#gate').hidden = entitled;
    if (!entitled) armMobileGate();
    return;
  }

  renderHead();
  // Guests cannot search/filter/sort — always full list order, free slice only.
  const list = entitled
    ? filteredRows()
    : (() => {
        const prev = { ...state };
        state.q = '';
        state.venue = '';
        state.failure = '';
        state.sort = 'date';
        state.dir = 'desc';
        const out = filteredRows();
        Object.assign(state, prev);
        return out;
      })();

  if (!list.length) {
    $('#rows').innerHTML = '';
    if (empty) {
      empty.hidden = false;
      empty.textContent = 'No cases match these filters.';
    }
    $('#gate').hidden = true;
    return;
  }

  if (empty) empty.hidden = true;

  // Guests: 20 free rows (last few gently blur), then locked teaser continuing
  // the same ramp so there is no hard sharp→blur cliff.
  if (entitled) {
    $('#rows').innerHTML = list.map((row) => rowHtml(row)).join('');
  } else {
    const shown = list.slice(0, FREE_ROWS + TEASER_ROWS);
    $('#rows').innerHTML = shown
      .map((row, i) => rowHtml(row, rampDepth(i), i >= FREE_ROWS))
      .join('');
  }
  // Always shown to guests: unlock CTA over the free list, not a blank table.
  $('#gate').hidden = entitled;
  if (!entitled) {
    // Ghosts first: the sheet arms off the first locked row, which is usually one.
    padGuestList();
    armMobileGate();
  }
}

/* On mobile the gate is a bottom sheet that stays off-screen until the visitor
 * has actually read some of the record. The ask lands when they reach the blur
 * — the point where the free rows run out — rather than on top of their first
 * screen. Scroll depth and a long dwell are backstops for anyone who never gets
 * that far. Desktop is unchanged. */
function armMobileGate() {
  const gate = $('#gate');
  if (!gate) return;
  if (!MOBILE_GATE_MQ.matches) {
    gate.classList.add('is-revealed');
    return;
  }
  if (mobileGateArmed || mobileGateDismissed) return;
  mobileGateArmed = true;
  gate.classList.remove('is-revealed');
  const scroller = $('.db-scroll');
  let revealed = false;
  let watcher = null;
  const teardown = () => {
    clearTimeout(timer);
    watcher?.disconnect();
    scroller?.removeEventListener('scroll', onScroll);
    window.removeEventListener('scroll', onScroll);
  };
  const reveal = () => {
    if (revealed || mobileGateDismissed) return;
    revealed = true;
    gate.classList.add('is-revealed');
    teardown();
  };
  teardownMobileGate = teardown;
  // The earned moment: the first locked row has climbed well into view, so the
  // open rows are behind them and the blur is what they are looking at.
  const firstLocked = $('#rows tr.locked');
  if (firstLocked && 'IntersectionObserver' in window) {
    watcher = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) reveal();
      },
      { root: scroller ?? null, rootMargin: '-30% 0px 0px' },
    );
    watcher.observe(firstLocked);
  }
  // Backstop for short corpora and filtered views, where there may be no locked
  // row to reach: roughly a screen and a half of reading, or half of whatever
  // the frame can actually scroll — a fixed depth alone never fires on a list
  // shorter than it, which is how the sheet stopped appearing.
  const depth = Math.max(600, Math.round(window.innerHeight * 1.5));
  const onScroll = () => {
    if (scroller) {
      const max = scroller.scrollHeight - scroller.clientHeight;
      if (max > 0 && scroller.scrollTop >= Math.min(depth, max * 0.5)) {
        reveal();
        return;
      }
    }
    if (window.scrollY > depth) reveal();
  };
  scroller?.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('scroll', onScroll, { passive: true });
  const timer = setTimeout(reveal, 20000);
}

/* The sheet covers the record on mobile, so it has to be closable — a visitor
 * who has read to the bottom should still be able to browse the free rows.
 * Staying closed for the session is the point; the hero and masthead still
 * carry submit and sign-in. */
function dismissMobileGate() {
  mobileGateDismissed = true;
  $('#gate')?.classList.remove('is-revealed');
  teardownMobileGate?.();
  teardownMobileGate = null;
}

/* ---------- events ---------- */

$('#gate-close')?.addEventListener('click', dismissMobileGate);

/* A resized viewport changes how many ghosts it takes to reach the bottom edge,
 * and re-laying them out throws away the row the sheet was watching. */
let repadTimer = null;
window.addEventListener('resize', () => {
  if (entitled) return;
  clearTimeout(repadTimer);
  repadTimer = setTimeout(() => {
    padGuestList();
    if ($('#gate')?.classList.contains('is-revealed')) return;
    teardownMobileGate?.();
    teardownMobileGate = null;
    mobileGateArmed = false;
    armMobileGate();
  }, 180);
});

$('#search')?.addEventListener('input', (e) => {
  if (!entitled) return;
  state.q = e.target.value;
  render();
});

$('#filter-venue')?.addEventListener('change', (e) => {
  if (!entitled) return;
  state.venue = e.target.value;
  render();
});

$('#filter-failure')?.addEventListener('change', (e) => {
  if (!entitled) return;
  state.failure = e.target.value;
  render();
});

$('#cols')?.addEventListener('click', (e) => {
  if (!entitled) return;
  const btn = e.target.closest('button[data-sort]');
  if (!btn) return;
  const key = btn.dataset.sort;
  state.dir = state.sort === key && state.dir === 'desc' ? 'asc' : 'desc';
  state.sort = key;
  render();
});

/* ---------- detail dialog (dense, movable, resizable) ---------- */

const detailEl = $('#detail');
const detailBodyEl = $('#detail-body');
const detailDragEl = $('#detail-drag');
const detailResize = $('#detail-resize');
const detailTitleEl = $('#detail-title');
const detailMetaEl = $('#detail-meta');
/** Row currently in the dialog, if any — used so a corpus reload does not reset it. */
let openCaseId = '';

function caseIdFromUrl() {
  try {
    return new URL(window.location.href).searchParams.get('s')?.trim() || '';
  } catch {
    return '';
  }
}

function idsMatch(a, b) {
  const norm = (value) => String(value || '').replace(/-/g, '').toLowerCase();
  return Boolean(a) && Boolean(b) && norm(a) === norm(b);
}

function findRowByCaseId(id) {
  if (!id) return null;
  return rows.find((row) => idsMatch(row.id, id)) || null;
}

function writeCaseIdToUrl(id) {
  const url = new URL(window.location.href);
  const current = url.searchParams.get('s') || '';
  if (id) {
    if (current === id) return;
    url.searchParams.set('s', id);
  } else if (!current) {
    return;
  } else {
    url.searchParams.delete('s');
  }
  history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
}

const extLink = (href, text) =>
  /^https?:\/\//i.test(href)
    ? `<a href="${esc(href)}" target="_blank" rel="noopener noreferrer">${esc(text)}</a>`
    : esc(text);

/* Turn bare URLs inside prose into links, escaping everything else. Trailing
 * sentence punctuation (and an unmatched ")") is kept out of the href. */
function linkify(text) {
  const urlRe = /https?:\/\/[^\s<]+/g;
  let out = '';
  let last = 0;
  let m;
  while ((m = urlRe.exec(text)) !== null) {
    let url = m[0];
    const trail = url.match(/[.,;:!?'"\]}]+$/)?.[0] ?? '';
    url = url.slice(0, url.length - trail.length);
    // Drop a trailing ")" only when the URL has no matching "(".
    let close = '';
    if (url.endsWith(')') && !url.includes('(')) {
      close = ')';
      url = url.slice(0, -1);
    }
    out += esc(text.slice(last, m.index)) + extLink(url, url) + esc(close + trail);
    last = m.index + m[0].length;
  }
  return out + esc(text.slice(last));
}

const detailBlock = (label, bodyHtml, cls = 'detail-block') =>
  `<section class="${cls}"><h3>${esc(label)}</h3>${bodyHtml}</section>`;

/**
 * Notion write-ups often end with "Sources: url, url…" and lead with
 * "Failure type: …". Those are already shown as the Sources list and the
 * Reason pill — strip them so the detail panel does not repeat.
 */
function cleanDetailProse(text) {
  let t = String(text ?? '');
  t = t.replace(/^Failure type:\s*[^.\n]+\.\s*/i, '');
  t = t.replace(/\n+\s*Sources:\s*[\s\S]*$/i, '');
  t = t.replace(/\s*Sources:\s*(https?:\/\/\S+(?:\s*,\s*https?:\/\/\S+)*)\s*$/i, '');
  return t.trim();
}

function detailSections(row) {
  const parts = [];
  for (const name of DETAIL_PROPS) {
    const cell = cellOf(row, name);
    if (!cell || (!cell.text && cell.value == null)) continue;
    const prose = cleanDetailProse(cell.text);
    if (!prose && !(cell.href || cell.type === 'url')) continue;
    const body =
      cell.href || cell.type === 'url'
        ? extLink(cell.href || cell.text, cell.text)
        : linkify(prose);
    const label = name === 'What went wrong' || name === 'What happened' ? 'Summary' : name;
    // Prefer Summary over What went wrong if both exist
    if (label === 'Summary' && parts.some((p) => p.includes('>Summary<'))) continue;
    parts.push(detailBlock(label, `<p>${body}</p>`));
  }
  for (const [name, cell] of Object.entries(row.cells || {})) {
    if (DETAIL_PROPS.includes(name) || DETAIL_SKIP.has(name)) continue;
    if (!cell || (!cell.text && cell.value == null)) continue;
    parts.push(detailBlock(name, `<p>${linkify(cell.text)}</p>`));
  }
  return parts.join('');
}

/** Sources are the receipts for the case — a plain bulleted list of URLs. */
function sourcesHtml(row) {
  const items = row.sources.filter((s) => s && (s.u || s.t));
  if (!items.length) return '';
  const lis = items
    .map((s) => (s.u ? `<li>${extLink(s.u, s.u)}</li>` : `<li>${esc(s.t || '')}</li>`))
    .join('');
  return detailBlock('Sources', `<ul class="source-list">${lis}</ul>`, 'detail-block detail-sources');
}

function resetDetailGeometry() {
  if (!detailEl) return;
  detailEl.removeAttribute('style');
  detailEl.classList.remove('is-positioned');
}

function fillDetail(row) {
  const failSlug = failureSlug(row.failure);
  detailTitleEl.textContent = row.market || 'Case';
  detailMetaEl.innerHTML = `
      <div><dt>Venue</dt><dd>${esc(row.venue || '—')}</dd></div>
      <div><dt>Date</dt><dd>${esc(fmtDate(row.date))}</dd></div>
      <div><dt>Est. Notional</dt><dd>${esc(fmtMoney(row.notional))}</dd></div>
      <div><dt>Reason</dt><dd>${
        row.failure
          ? `<span class="db-pill db-pill--failure" data-failure="${esc(failSlug)}">${esc(row.failure)}</span>`
          : '—'
      }</dd></div>`;
  // Sources sit inside the grid so its gap owns the spacing.
  detailBodyEl.innerHTML = `<div class="detail-fields">${detailSections(row)}${sourcesHtml(row)}</div>`;
}

function openDetailRow(row, { syncUrl = true } = {}) {
  if (!row || !detailEl || !detailBodyEl) return false;
  fillDetail(row);
  detailBodyEl.scrollTop = 0;
  resetDetailGeometry();
  if (!detailEl.open) detailEl.showModal();
  openCaseId = row.id;
  if (syncUrl) writeCaseIdToUrl(row.id);
  return true;
}

function openDetail(tr) {
  if (!tr || tr.classList.contains('locked')) return;
  openDetailRow(findRowByCaseId(tr.dataset.id));
}

/** Open the case named in `?s=` once that row is in the loaded corpus. */
function tryOpenCaseFromUrl() {
  const id = caseIdFromUrl();
  if (!id) return;
  if (detailEl?.open && idsMatch(openCaseId, id)) return;
  const row = findRowByCaseId(id);
  if (row) openDetailRow(row, { syncUrl: false });
}

$('#detail-close')?.addEventListener('click', () => detailEl?.close());
detailEl?.addEventListener('close', () => {
  openCaseId = '';
  if (caseIdFromUrl()) writeCaseIdToUrl('');
});
window.addEventListener('popstate', tryOpenCaseFromUrl);

$('#rows')?.addEventListener('click', (e) => {
  if (e.target.closest('a')) return;
  openDetail(e.target.closest('tr'));
});

$('#rows')?.addEventListener('keydown', (e) => {
  if (e.key !== 'Enter' && e.key !== ' ') return;
  const tr = e.target.closest('tr');
  if (!tr || tr !== e.target) return;
  e.preventDefault();
  openDetail(tr);
});

/* Light dismiss. A click's target is the common ancestor of press and release,
 * so a drag from the header to the body also reports the dialog. Latch where
 * the press landed instead: only a press that started on the backdrop closes. */
function closeOnBackdrop(dialogEl) {
  if (!dialogEl) return;
  let pressedBackdrop = false;
  dialogEl.addEventListener('pointerdown', (e) => {
    pressedBackdrop = e.target === dialogEl;
  });
  dialogEl.addEventListener('click', () => {
    if (pressedBackdrop) dialogEl.close();
  });
}

closeOnBackdrop(detailEl);

/** Freeze the centred panel at its current box so it can be moved or sized. */
function pinGeometry() {
  const rect = detailEl.getBoundingClientRect();
  if (!detailEl.classList.contains('is-positioned')) {
    detailEl.style.width = `${rect.width}px`;
    detailEl.style.height = `${rect.height}px`;
    detailEl.style.top = `${rect.top}px`;
    detailEl.style.left = `${rect.left}px`;
    detailEl.classList.add('is-positioned');
  }
  return rect;
}

// Drag by the title block
if (detailDragEl && detailEl) {
  let drag = null;
  detailDragEl.addEventListener('pointerdown', (e) => {
    if (e.target.closest('.detail-close')) return;
    pinGeometry();
    const r = detailEl.getBoundingClientRect();
    // Width is fixed for the whole gesture; reading it per move forces layout.
    drag = { ox: e.clientX - r.left, oy: e.clientY - r.top, w: r.width };
    detailDragEl.setPointerCapture(e.pointerId);
  });
  detailDragEl.addEventListener('pointermove', (e) => {
    if (!drag) return;
    const maxX = window.innerWidth - drag.w - 8;
    const maxY = window.innerHeight - 40;
    detailEl.style.left = `${Math.min(maxX, Math.max(8, e.clientX - drag.ox))}px`;
    detailEl.style.top = `${Math.min(maxY, Math.max(8, e.clientY - drag.oy))}px`;
  });
  detailDragEl.addEventListener('pointerup', () => { drag = null; });
  detailDragEl.addEventListener('pointercancel', () => { drag = null; });
}

// Resize from corner handle
if (detailResize && detailEl) {
  let resize = null;
  detailResize.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = pinGeometry();
    resize = { startX: e.clientX, startY: e.clientY, w: rect.width, h: rect.height };
    detailResize.setPointerCapture(e.pointerId);
  });
  detailResize.addEventListener('pointermove', (e) => {
    if (!resize) return;
    const dw = e.clientX - resize.startX;
    const dh = e.clientY - resize.startY;
    detailEl.style.width = `${Math.min(window.innerWidth - 16, Math.max(320, resize.w + dw))}px`;
    detailEl.style.height = `${Math.min(window.innerHeight - 16, Math.max(240, resize.h + dh))}px`;
  });
  detailResize.addEventListener('pointerup', () => { resize = null; });
  detailResize.addEventListener('pointercancel', () => { resize = null; });
}

closeOnBackdrop($('#submit'));

/* ---------- submission ---------- */

const dialog = $('#submit');
const form = $('#case-form');
const steps = [...form.querySelectorAll('[data-step]')];
const formTitle = $('#form-title');
const formHelp = $('#form-help');
const back = $('#form-back');
const next = $('#form-next');
const submit = $('#form-submit');
const status = $('.form-status');
let step = 0;

/* The brief itself lives at /disputes/prompt.md so it can be revised without
 * asking anyone to re-copy a wall of text. What the button copies is a pointer:
 * short enough to paste anywhere, and it fetches the current instructions. */
const DISPUTE_AGENT_PROMPT =
  `Fetch and follow the instructions at ${PUBLIC_PROMPT_URL} to help me file a ` +
  `prediction-market resolution dispute with Adjacent.`;

/* navigator.clipboard exists only in a secure context, so a plain-http origin
 * (LAN previews) and some in-app browsers have no clipboard API at all. The
 * selection-based copy predates it and still works there. */
function copyBySelection(text) {
  const scratch = document.createElement('textarea');
  scratch.value = text;
  scratch.readOnly = true;
  // Off-screen but focusable: display:none or hidden cannot be selected.
  scratch.style.cssText = 'position:fixed;top:-1000px;left:0;opacity:0;font-size:16px';
  document.body.append(scratch);
  scratch.select();
  scratch.setSelectionRange(0, text.length);
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  scratch.remove();
  return ok;
}

async function copyAgentPrompt(event) {
  const button = event.currentTarget;
  if (button.dataset.busy) return;
  const original = button.dataset.label || button.textContent;
  button.dataset.label = original;
  button.dataset.busy = '1';
  let copied = false;
  try {
    await navigator.clipboard.writeText(DISPUTE_AGENT_PROMPT);
    copied = true;
  } catch {
    copied = copyBySelection(DISPUTE_AGENT_PROMPT);
  }
  button.textContent = copied ? 'Copied' : 'Copy failed';
  window.setTimeout(() => {
    button.textContent = original;
    delete button.dataset.busy;
  }, 1600);
}

// Guests and staff both submit from hero or gate — no account required.
$('#open-agent')?.addEventListener('click', copyAgentPrompt);
$('#member-agent')?.addEventListener('click', copyAgentPrompt);

/* Newsletter-style subscribe: email → adjacent.markets/subscribe?email= */
$('#gate-subscribe')?.addEventListener('submit', (event) => {
  event.preventDefault();
  const input = $('#gate-email');
  const email = input?.value?.trim() ?? '';
  if (!email || !input?.checkValidity()) {
    input?.reportValidity();
    return;
  }
  const url = new URL('https://adjacent.markets/subscribe');
  url.searchParams.set('email', email);
  window.location.assign(url.toString());
});

const STEP_COPY = [
  ['Where did it happen?', 'Start with the market, article, or post that describes the dispute.'],
  ['What was the market?', 'We read the link and made a best guess. Correct anything that is wrong.'],
  ['What went wrong?', 'The useful detail is the gap between the rule and the event.'],
  ['How can we reach you?', 'Optional. Anonymous submissions are welcome.'],
];

function parseSource(value) {
  try {
    const url = new URL(value);
    const host = url.hostname.replace(/^www\./, '').toLowerCase();
    const marketHosts = [
      'polymarket.com', 'kalshi.com', 'limitless.exchange', 'myriad.markets',
      'manifold.markets', 'metaculus.com', 'predictit.org',
    ];
    const marketHost = marketHosts.find((h) => host === h || host.endsWith(`.${h}`));
    const slug = url.pathname.split('/').filter(Boolean).pop()?.replace(/[-_]+/g, ' ');
    const title = slug ? slug.replace(/\b\w/g, (c) => c.toUpperCase()) : '';
    if (marketHost || host.startsWith('opinion.')) {
      const venue = marketHost ? marketHost.split('.')[0] : 'opinion';
      return { kind: 'market', venue, title };
    }
    const tweet = /^(?:x|twitter)\.com$/.test(host) && url.pathname.split('/').includes('status');
    if (tweet) return { kind: 'tweet', handle: url.pathname.split('/')[1] ?? '' };
    return { kind: 'article', publisher: host, title };
  } catch {
    return null;
  }
}

function showStep(nextStep) {
  step = nextStep;
  steps.forEach((field, i) => { field.hidden = i !== step; });
  [formTitle.textContent, formHelp.textContent] = STEP_COPY[step];
  back.hidden = step === 0;
  next.hidden = step === steps.length - 1;
  submit.hidden = step !== steps.length - 1;
  status.textContent = '';
  steps[step].querySelector('input, textarea, select')?.focus();
}

function validateCurrent() {
  const field = steps[step].querySelector('input, textarea, select');
  const error = steps[step].querySelector('.field-error');
  if (field.checkValidity()) {
    error.textContent = '';
    return true;
  }
  error.textContent = field.validity.typeMismatch
    ? 'Enter a complete URL.'
    : field.validity.tooShort
      ? 'Add a bit more detail (a few sentences).'
      : 'This field is required.';
  field.focus();
  return false;
}

function openSubmitDialog() {
  form.reset();
  showStep(0);
  dialog.showModal();
}

// Guests reach this from the gate; entitled members from the hero button.
$('#open-submit')?.addEventListener('click', openSubmitDialog);
$('#member-submit')?.addEventListener('click', openSubmitDialog);

next?.addEventListener('click', () => {
  if (!validateCurrent()) return;
  if (step === 0) {
    const parsed = parseSource($('#source').value);
    if (!parsed) return;
    if (parsed.title) $('#market').value = parsed.title;
    if (parsed.venue) {
      const venue = [...$('#venue').options].find(
        (option) => option.value.toLowerCase() === parsed.venue.toLowerCase(),
      );
      if (venue) $('#venue').value = venue.value;
    }
  }
  showStep(step + 1);
});

back?.addEventListener('click', () => showStep(step - 1));

form?.addEventListener('submit', async (event) => {
  event.preventDefault();
  if (!validateCurrent()) return;
  const data = Object.fromEntries(new FormData(form));
  const problemText = String(data.problem || '').trim();
  // Mirror the worker's free-form floor so we fail before the network round-trip.
  if (problemText.length < 40) {
    status.textContent = 'Say a bit more about what went wrong (at least a few sentences).';
    showStep(2);
    $('#problem')?.focus();
    return;
  }
  const parsed = parseSource(data.source);
  submit.disabled = true;
  status.textContent = 'Sending…';
  try {
    const response = await fetch(apiUrl('/api/submissions'), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ...data, sourceKind: parsed?.kind }),
    });
    let payload = null;
    try {
      payload = await response.json();
    } catch {
      /* non-JSON error body */
    }
    if (!response.ok) {
      const detail = payload?.error || payload?.message || `HTTP ${response.status}`;
      throw new Error(detail);
    }
    steps.forEach((field) => { field.hidden = true; });
    formTitle.textContent = 'Case received.';
    formHelp.textContent = 'We’ll review the evidence and add it to the record if it qualifies.';
    status.textContent = '';
    back.hidden = true;
    next.hidden = true;
    submit.hidden = true;
  } catch (error) {
    const msg = error instanceof Error ? error.message : '';
    status.textContent = msg && msg !== 'Failed to fetch'
      ? `Could not send this case: ${msg}`
      : 'Could not send this case. Please try again.';
    submit.disabled = false;
  }
});

/* ---------- auth and live record ---------- */

function fromSample(s, failureTypes) {
  const failure = failureTypes[s.type]?.label ?? 'Uncoded';
  const date = s.date?.length === 7 ? `${s.date}-01` : (s.date ?? '');
  const venue = titleCase(s.venue);
  return {
    id: s.id,
    url: undefined,
    title: s.title,
    market: s.title,
    venue,
    date,
    failure,
    notional: typeof s.notional === 'number' ? s.notional : null,
    sources: Array.isArray(s.sources) ? s.sources : [],
    cells: {
      Market: { type: 'title', value: s.title, text: s.title },
      Venue: { type: 'select', value: venue, text: venue },
      Month: { type: 'date', value: date, text: date },
      'Failure type': { type: 'select', value: failure, text: failure },
      Notional: {
        type: 'number',
        value: typeof s.notional === 'number' ? s.notional : null,
        text: typeof s.notional === 'number' ? String(s.notional) : '',
      },
      Summary: { type: 'rich_text', value: s.summary ?? '', text: s.summary ?? '' },
      Outcome: { type: 'rich_text', value: s.outcome ?? '', text: s.outcome ?? '' },
      Lesson: { type: 'rich_text', value: s.lesson ?? '', text: s.lesson ?? '' },
    },
  };
}

/** Everything derived from a fresh corpus. Runs per load, not per render. */
function onRowsLoaded() {
  state.sort = 'date';
  state.dir = 'desc';
  fillFilters();
}

async function loadSampleFallback() {
  const { sample, failureTypes } = await import('./sample.js');
  rows = sample.map((s) => fromSample(s, failureTypes));
  onRowsLoaded();
  console.info('Using local sample corpus (%s rows) — live Notion returned empty or failed.', rows.length);
}

/** Primary email on the Clerk user, if any. */
function clerkEmail() {
  return (
    clerk?.user?.primaryEmailAddress?.emailAddress ||
    clerk?.user?.emailAddresses?.[0]?.emailAddress ||
    ''
  );
}

/** Staff emails on Adjacent domains count as internal even if org list is lagging. */
function isAdjacentEmail() {
  return /@(?:adjacentresearch\.xyz|adjacent\.markets)$/i.test(clerkEmail());
}

function membershipOrgId(m) {
  return m?.organization?.id || m?.organization?.organization?.id || m?.id || '';
}

function membershipList(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw.data)) return raw.data;
  return [];
}

/**
 * Make the session token carry the Adjacent org, which is what the API checks.
 * Best-effort: a non-member simply stays on the free slice. The return value is
 * advisory — entitlement is whatever the API says on the next fetch.
 */
async function ensureAdjacentOrg() {
  if (!clerk?.isSignedIn) return false;

  if (CLERK_ORG_ID && clerk.user) {
    try {
      if (clerk.organization?.id !== CLERK_ORG_ID) {
        let memberships = membershipList(clerk.user.organizationMemberships);
        if (typeof clerk.user.getOrganizationMemberships === 'function') {
          const page = await clerk.user.getOrganizationMemberships({ limit: 100 });
          memberships = membershipList(page).length ? membershipList(page) : memberships;
        }
        const member =
          memberships.some((m) => membershipOrgId(m) === CLERK_ORG_ID) || isAdjacentEmail();
        if (member) {
          try {
            await clerk.setActive({ organization: CLERK_ORG_ID });
          } catch (error) {
            console.info('Adjacent org not activated:', error?.message || error);
          }
        }
      }
    } catch (error) {
      console.warn('Org activation skipped:', error);
    }
  }

  return true;
}

async function sessionAuthHeaders() {
  const headers = {};
  try {
    const token = await clerk?.session?.getToken?.();
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch {
    /* guest or clerk not ready */
  }
  return headers;
}

async function loadDisputes() {
  // The server decides how much of the corpus we get: org session → everything,
  // otherwise the free slice. `entitled` comes back with the rows so the gate
  // reflects what we actually hold rather than a guess made in the browser.
  const response = await fetch(apiUrl('/api/disputes'), {
    headers: await sessionAuthHeaders(),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const data = await response.json();
  const list = Array.isArray(data.rows)
    ? data.rows
    : Array.isArray(data.disputes)
      ? data.disputes
      : null;
  if (!list) throw new Error('bad record');
  rows = list.map(normalizeRow);
  entitled = data.entitled === true;
  onRowsLoaded();
}

function finishLoading() {
  render();
  tableSection?.classList.remove('is-loading');
  tryOpenCaseFromUrl();
}

function wireAuthButtons() {
  const loginLinks = ['#auth-login', '#auth-login-gate']
    .map((sel) => $(sel))
    .filter(Boolean);
  if (!clerk || !loginLinks.length) return;

  const syncLabels = () => {
    const signedIn = clerk.isSignedIn;
    for (const el of loginLinks) {
      // Gate keeps "Sign in" wording; masthead flips Login ↔ Account.
      if (el.id === 'auth-login') el.textContent = signedIn ? 'Account' : 'Login';
      else if (!signedIn) el.textContent = 'Sign in';
      else el.textContent = 'Account';
      // Real href so middle-click works. Signed-out → billing (auth redirect);
      // signed-in → account settings.
      if (el.tagName === 'A') el.href = signedIn ? ACCOUNT_URL : LOGIN_URL;
    }
  };
  // No click interceptor — navigation is plain href to adjacent.markets.
  clerk.addListener(syncLabels);
  syncLabels();
}

/**
 * Clerk writes __client_uat as a non-HttpOnly cookie: 0 (or absent) when
 * signed out, a positive timestamp when a session exists. Reading it lets us
 * tell a returning member from a guest before the SDK bundle loads.
 */
function hasClerkSession() {
  const m = document.cookie.match(/(?:^|;\s*)__client_uat=(\d+)/);
  return Boolean(m) && Number(m[1]) > 0;
}

/** Optimistically reflect a known session so the header doesn't flash Login. */
function primeAuthLabel() {
  for (const sel of ['#auth-login', '#auth-login-gate']) {
    const el = $(sel);
    if (!el) continue;
    el.textContent = 'Account';
    if (el.tagName === 'A') el.href = ACCOUNT_URL;
  }
}

/**
 * Re-ask the server for the corpus now that the session state is known.
 *
 * The first load happens before Clerk is ready, so it is always an anonymous
 * request that comes back as the free slice. Once a session resolves we
 * activate the org (so the JWT carries org_id) and fetch again — that response
 * is what promotes the table, since only the server can widen it.
 */
async function refreshEntitlement() {
  const wasEntitled = entitled;
  if (!clerk?.isSignedIn) {
    // Signed out (or signed out mid-session): drop back to the free slice.
    if (wasEntitled) await loadDisputes().catch((e) => console.warn('Corpus reload failed:', e));
    else entitled = false;
    return;
  }
  await ensureAdjacentOrg();
  await loadDisputes().catch((e) => console.warn('Corpus reload failed:', e));
}

async function initClerk() {
  if (!CLERK_PUBLISHABLE_KEY || clerk) return;
  try {
    const { Clerk } = await import('@clerk/clerk-js');
    clerk = new Clerk(CLERK_PUBLISHABLE_KEY);
    // Production key often rejects localhost; don't block the page on it.
    await Promise.race([
      clerk.load(),
      new Promise((_, reject) => {
        window.setTimeout(() => reject(new Error('Clerk load timeout')), 4000);
      }),
    ]);
    wireAuthButtons();
    // Apply current session immediately — addListener does not always re-fire
    // for the initial state, which left signed-in users on the free tier.
    await refreshEntitlement();
    clerk.addListener(() => {
      void (async () => {
        await refreshEntitlement();
        finishLoading();
      })();
    });
  } catch (error) {
    console.warn('Clerk unavailable:', error);
    clerk = null;
  }
}

async function bootstrap() {
  // Load the public record first so guests always get FREE_ROWS + blur.
  try {
    await loadDisputes();
  } catch (error) {
    console.warn('Live Notion record unavailable:', error);
  }
  if (!rows.length && IS_DEV) {
    await loadSampleFallback().catch((e) => console.warn('Sample fallback failed:', e));
  }

  // Always resolve Clerk before first paint of entitlement UI when configured.
  if (CLERK_PUBLISHABLE_KEY) {
    if (hasClerkSession()) primeAuthLabel();
    await initClerk();
  } else {
    entitled = false;
  }

  finishLoading();
}

void bootstrap();
